import {useState} from 'react';
import {faUserCircle} from '@fortawesome/pro-regular-svg-icons';
import useTranslation from 'next-translate/useTranslation';

import {
  CorpAccountSelectionModal,
  CorpAccountSelectionModalProvider,
  VISIBLE_CORP_ACCOUNT_LIMIT,
} from '@/components/global/shared/CorpAccountDropdown';
import useCorporateAccount from '@/hooks/useCorporateAccount';
import useEzRewardsEligibility from '@/hooks/useEzRewardsEligibility';
import useIdentity from '@/hooks/useIdentity';
import useOrderingExperience from '@/hooks/useOrderingExperience';
import AppBarIconButton from '../AppBarIconButton';
import {NAVLINK_TRACKING} from '../appBarTracking';
import useNavbarVersion from '../hooks/useNavbarVersion';
import useNavLinkTracking from '../hooks/useNavlinkTracking';
import {UserMenu} from '../Menus';
import MenuPopover from '../Menus/MenuPopover';
import ProfileHoverTracker from './ProfileHoverTracker';
import {UserProfileButtonDisplayMode} from './types';
import UserProfileButton from './UserProfileButton';

const User: React.FC<unknown> = () => {
  const [isTabletMenuOpen, setIsTabletMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const {data: identityData} = useIdentity();
  const {balanceEnabled} = useEzRewardsEligibility();
  const {isEzOrdering} = useOrderingExperience();
  const {t} = useTranslation('app-bar');
  const {trackRumAction} = useNavLinkTracking();
  const version = useNavbarVersion();

  const handleProfileClick = () => {
    trackRumAction({datadogAction: NAVLINK_TRACKING.profile.clicked, version});
  };
  const ezRewardsBalance = identityData?.me?.consumerAccount?.ezRewardsAccount?.balance;
  const firstName = identityData?.me?.consumerAccount?.firstName || '';

  const {corporateDisplayName, corporateAccountsCount} = useCorporateAccount();

  const getDisplayMode = (): UserProfileButtonDisplayMode => {
    if (corporateDisplayName) {
      return {mode: 'corporate', displayName: corporateDisplayName};
    }
    if (balanceEnabled && !isEzOrdering) {
      return {mode: 'ezRewards', balance: ezRewardsBalance || 0};
    }
    return {mode: 'none'};
  };

  return (
    <CorpAccountSelectionModalProvider>
      <div className="hidden appbar-lg:block appbar-xl:hidden">
        <MenuPopover
          ariaLabel={t('user.label')}
          arrowClasses="right-0.5"
          className="min-w-80"
          content={<UserMenu closeMenu={() => setIsTabletMenuOpen(false)} />}
          isOpen={isTabletMenuOpen}
          onOpenChange={setIsTabletMenuOpen}
        >
          <AppBarIconButton
            aria-label={t('user.label')}
            data-testid="mobile-profile-nav-bar-item"
            icon={faUserCircle}
            onClick={handleProfileClick}
            title={t('user.label')}
            variant="secondary"
          />
        </MenuPopover>
      </div>

      <div className="hidden appbar-xl:block">
        <MenuPopover
          ariaLabel={t('user.label')}
          arrowClasses="right-0.5"
          className="min-w-80"
          content={
            <>
              <ProfileHoverTracker version={version} />
              <UserMenu closeMenu={() => setIsDesktopMenuOpen(false)} />
            </>
          }
          isOpen={isDesktopMenuOpen}
          onOpenChange={setIsDesktopMenuOpen}
        >
          <UserProfileButton
            displayMode={getDisplayMode()}
            firstName={firstName}
            onClick={handleProfileClick}
          />
        </MenuPopover>
      </div>
      {corporateAccountsCount > VISIBLE_CORP_ACCOUNT_LIMIT && <CorpAccountSelectionModal />}
    </CorpAccountSelectionModalProvider>
  );
};

export default User;
